import React, { FC, useCallback, useMemo, useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import FroalaEditor from 'react-froala-wysiwyg';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import { merge } from 'lodash';
import config, { baseConfig, froalaTheme } from './config';
import FroalaContentWrapper from './FroalaContentWrapper';
import ImageUploadFailedDialog from './ImageUploadFailedDialog';
import { useFroalaTranslations } from './froalaTranslations';
import { useUser } from '../../hooks';
import LabeledCheckbox from '../LabeledCheckbox/LabeledCheckbox';
import { styled } from '@material-ui/styles';
import { UrlParams } from '../../types';
import { useParams } from 'react-router-dom';
import {
  UploadFroalaImageResponse,
  usePostUploadFroalaImageMutation,
} from '../../slices/froala/entity';
import { useAppSelector } from '../../hooks/useAppDispatch';

const MAX_IMAGE_SIZE: number = 1024 * 1024 * 5;

export type ImageUploadError = {
  title: string;
  message: string;
};

export type WysiwygType =
  | 'normal'
  | 'studyGuideQuestion'
  | 'studyGuideConcepts'
  | 'feedback'
  | 'notes';

export type WysiwygEditorProps = {
  id: string;
  value: string;
  onPrintableChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  type?: WysiwygType;
  className?: string;
  applyNoteStyles?: boolean;
  forceTitleOnTop?: boolean;
  onLoadRef?: (ref: React.MutableRefObject<FroalaEditor | null>) => void;
  isPrintable?: boolean;
  handleBlur?: (content: string) => void;
  toolbarSticky?: boolean;
};

const ClearFix = styled('div')({
  clear: 'both',
});

const WysiwygEditor: FC<WysiwygEditorProps> = ({
  id,
  value,
  onPrintableChange,
  readOnly,
  type = 'normal',
  className,
  applyNoteStyles,
  forceTitleOnTop = false,
  onLoadRef,
  isPrintable,
  handleBlur = null,
  toolbarSticky = true,
}) => {
  const { t } = useTranslation();
  const translations = useFroalaTranslations(t);
  const { isAuthor } = useUser();
  const { bookId, trajectoryId } = useParams<UrlParams>();
  const [imageError, setImageError] = useState<ImageUploadError>();
  const froalaRef = React.useRef<FroalaEditor | null>(null);
  const fullscreenMode = useAppSelector((state) => state.fullscreenMode.fullscreenMode);

  useEffect(() => {
    if (onLoadRef && froalaRef) onLoadRef(froalaRef);
  }, [froalaRef, onLoadRef]);

  const studyGuideQuestionConfig = useCallback(
    (readOnly: boolean = false) => ({
      ...baseConfig(readOnly),
      toolbarButtons: {
        moreText: { buttons: ['bold', 'italic'] },
        moreRich: {
          buttons: ['wirisEditor', 'wirisChemistry', 'clearFormatting'],
          buttonsVisible: 2,
        },
      },

      // Plugin Services
      mathTypeParameters: {
        serviceProviderProperties: {
          URI: process.env.REACT_APP_WIRIS_INTEGRATION_URL,
          server: 'php',
        },
      },
    }),
    []
  );
  const studyGuideConceptsConfig = useCallback(
    (readOnly: boolean = false) => ({
      ...baseConfig(readOnly),
      toolbarButtons: {
        moreRich: {
          buttons: ['wirisEditor', 'wirisChemistry', 'clearFormatting'],
          buttonsVisible: 2,
        },
      },

      // Plugin Services
      mathTypeParameters: {
        serviceProviderProperties: {
          URI: process.env.REACT_APP_WIRIS_INTEGRATION_URL,
          server: 'php',
        },
      },
    }),
    []
  );

  const feedbackConfig = useCallback(
    (readOnly: boolean = false) => ({
      ...baseConfig(readOnly, false),
      toolbarButtons: {
        moreText: {
          buttons: ['bold', 'italic', 'underline', 'textColor', 'backgroundColor'],
          buttonsVisible: 5,
        },
        moreParagraph: {
          buttons: ['formatOLSimple'],
        },
        moreRich: {
          buttons: ['insertLink', 'insertImage', 'insertVideo', 'clearFormatting'],
        },
      },
      charCounterCount: true,
      charCounterMax: 600,
    }),
    []
  );

  const [uploadImageMutation] = usePostUploadFroalaImageMutation();

  const fullConfig = useMemo(
    () =>
      merge({}, config(translations, isAuthor, readOnly), {
        heightMin: '20vh',
        imageUpload: true,
        events: {
          'image.beforeUpload': function (imagesFiles: File[]) {
            var editor: any = this;
            if (imagesFiles.length === 0) {
              return;
            }
            if (imagesFiles[0].size >= MAX_IMAGE_SIZE) {
              setImageError({
                title: t('froala.imageUpload.maxSizeExceeded.title'),
                message: t('froala.imageUpload.maxSizeExceeded.message'),
              });
              return false;
            }
            editor.popups.hideAll();
            const currentImage = editor.image.get();

            new Promise((resolve, reject) => {
              const reader = new FileReader();

              reader.onload = async () => {
                resolve(reader.result);
                if (bookId && trajectoryId) {
                  const body = { photo: reader.result as string };
                  await uploadImageMutation({ bookId, trajectoryId, body })
                    .unwrap()
                    .then((payload: UploadFroalaImageResponse) => {
                      editor.image.insert(
                        payload.data.image_url,
                        null,
                        { src: payload.data.image_url, srcset: payload.data.image_url },
                        currentImage
                      );
                    })
                    .catch(() => {
                      editor.image.insert(
                        reader.result as string,
                        true,
                        { srcset: '' },
                        currentImage
                      );
                    });
                }
              };

              reader.onerror = (error) => {
                reject(error);
                setImageError({
                  title: t('froala.imageUpload.tooManyAttempts.title'),
                  message: t('froala.imageUpload.tooManyAttempts.message'),
                });
              };

              reader.readAsDataURL(imagesFiles[0]);
            });
            return false;
          },
          blur: function (e: any, editor: any) {
            if (handleBlur && e?.currentTarget?.innerHTML) {
              handleBlur(e.currentTarget.innerHTML);
            }
          },
        },
      }),
    [translations, isAuthor, readOnly, t, bookId, trajectoryId, uploadImageMutation, handleBlur]
  );

  const notesConfig = useCallback(
    (readOnly: boolean = false) => ({
      ...config(translations, isAuthor, readOnly),
      htmlRemoveTags: ['script', 'style', 'base', 'iframe', 'form', 'input', 'button', 'select'],
      events: {
        'image.beforePasteUpload': function (img: HTMLImageElement) {
          window.alert('Je kan geen afbeeldingen plakken in tekstblok');
          img.remove();
          return false;
        },
        blur: function (e: any, editor: any) {
          if (handleBlur && e?.currentTarget?.innerHTML) {
            handleBlur(e.currentTarget.innerHTML);
          }
        },
      },
      toolbarButtons: {
        moreText: {
          buttons: ['bold', 'italic', 'underline'],
          buttonsVisible: 3,
        },
        moreParagraph: {
          buttons: ['textColor', 'formatOLSimple', 'formatUL'],
          buttonsVisible: 3,
        },
        moreRich: {
          buttons: [
            'backgroundColor',
            'insertTable',
            'insertHR',
            'insertLink',
            'wirisEditor',
            'wirisChemistry',
            'clearFormatting',
          ],
          buttonsVisible: 0,
        },
      },
    }),
    [translations, isAuthor, handleBlur]
  );

  return (
    <>
      <FroalaContentWrapper
        id={id}
        froalaTheme={froalaTheme}
        translations={translations}
        fullScreenEnabled={fullscreenMode}
        className={className}
        applyNoteStyles={applyNoteStyles}
        forceTitleOnTop={forceTitleOnTop}
        toolbarSticky={toolbarSticky}
      >
        {readOnly ? (
          <FroalaEditorView model={value} />
        ) : (
          <FroalaEditor
            ref={(ref) => (froalaRef.current = ref)}
            tag="textarea"
            config={
              {
                normal: fullConfig,
                studyGuideQuestion: studyGuideQuestionConfig(readOnly),
                studyGuideConcepts: studyGuideConceptsConfig(readOnly),
                feedback: feedbackConfig(readOnly),
                notes: notesConfig(readOnly),
              }[type]
            }
            model={value}
          />
        )}
        {imageError && (
          <ImageUploadFailedDialog
            onClose={() => setImageError(undefined)}
            title={imageError.title}
            message={imageError.message}
          />
        )}
        <ClearFix />
        {!readOnly && isPrintable !== undefined && onPrintableChange && (
          <LabeledCheckbox
            label="Opnemen in print"
            tooltipMessage="Vink uit als deze blok niet mee mag in print"
            checked={isPrintable}
            hideOnPrint={onPrintableChange}
          />
        )}
      </FroalaContentWrapper>
    </>
  );
};

export default WysiwygEditor;
